/**
 * serialSave — run a panel's saves strictly one after another.
 *
 * WHY (Walter 2026-09-13). He got a conflict band with both devices online.
 * The history labelled it (conflict) at 3:19 PM, and his phone's log (UTC)
 * lined up to the second:
 *
 *   19:19:16  saved        chars=32291
 *   19:19:17  save-failed  failed-precondition: "the stored version
 *                          (1789327156934593) does not match the required
 *                          base version (1789327156091095)"
 *   19:19:20  saved-via-api   ← records (conflict), raises the band
 *
 * Those version stamps are 0.84s apart: the phone's OWN previous save
 * invalidated the second one. No other device was involved.
 *
 * The panels' flushSave captures the conflict base when a save is SENT and
 * advances it only after the write RETURNS. Saves fire from the 4s debounce,
 * visibilitychange, pagehide, the force-sync tap, unmount, and several
 * explicit commits — and nothing made a second one wait. So a save sent while
 * the previous one was still in flight (easily, on a slow phone link) carried
 * a base the server had already moved past, and the three-way merge read the
 * phone's own consecutive edits to one line as a same-line clash.
 *
 * The fix is ordering, not detection: each save waits for the one before it,
 * and the task reads the base when it STARTS — by which time the previous
 * save has advanced it. Nothing about what a save writes changes.
 *
 * Pure and framework-free so the ordering itself is unit-tested; the panels
 * only hold one runner each.
 */

/**
 * createSerialRunner() → run(task)
 *
 * `task` is a function (sync or async). It does not START until every task
 * handed to this runner before it has settled. `run` returns a promise for
 * this task's own result, so callers that already `await flushSave()` keep
 * waiting for their save — and everything queued ahead of it.
 *
 * A task that throws rejects only its own promise; the chain carries on, so
 * one failed save can never wedge every save after it.
 */
export function createSerialRunner() {
  let tail = Promise.resolve();
  return function run(task) {
    const result = tail.then(() => task());
    // Keep the chain alive past a failure; the caller still sees the rejection
    // through `result`.
    tail = result.catch(() => {});
    return result;
  };
}
